/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@quarry/inventory';
import ProviderRS2 from '@react/react-spectrum/Provider';

import { DialogContainer } from '@adobe/react-spectrum';

import CategoryPicker from '../CategoryPicker/CategoryPicker';
import CategoryPickerContextProvider, { useCategoryPickerContext } from '../CategoryPicker/CategoryPickerContext';
import SelectionContextProvider, { useSelectionContext } from '../common/SelectionContext';

import { useGraphqlSchemaContext } from '../GraphqlSchemaContext/GraphqlSchemaContext';
import { defineMessages, useIntl } from 'react-intl';
import ProductPickerContextProvider, { useProductPickerContext } from '../ProductPicker/ProductPickerContext';
import ProductPicker from '../ProductPicker/ProductPicker';

// i18n
const titles = defineMessages({
    pickCategory: {
        id: 'dialog.pickCategory',
        defaultMessage: 'Pick Category'
    },
    pickProduct: {
        id: 'dialog.pickProduct',
        defaultMessage: 'Pick Product'
    },
    missingConfigTitle: {
        id: 'picker.missingConfigTitle',
        defaultMessage: 'Missing Configuration'
    },
    missingConfigDesc: {
        id: 'error.missingConfigDesc',
        defaultMessage: 'Could not find a valid configuration.'
    }
});

const pickerEvents = {
    selectionEvent: 'cifPickerSelection',
    closeEvent: 'cifPickerClosed',
    errorEvent: 'cifPickerError'
};

const CategoryPickerModal = props => {
    const { formatMessage } = useIntl();
    const [, , [config]] = useGraphqlSchemaContext();
    const { show, onConfirm, onClose, multiple, selectionId } = props;
    const [{ pickerSelection }] = useSelectionContext();
    const [, { getSelectionId, getCategories }] = useCategoryPickerContext();

    const confirmSelection = async () => {
        const categories = await getCategories(Array.from(pickerSelection));

        const selections = categories.map(item => {
            const [value, type] = getSelectionId(item, selectionId, config.supportUID);
            return { value, type, item };
        });

        onConfirm(selections);
        onClose();
    };
    return (
        <DialogContainer type="fullscreen">
            {show && (
                <Dialog title={formatMessage(titles.pickCategory)} onClose={onClose} onConfirm={confirmSelection}>
                    <ProviderRS2 theme="light">
                        <CategoryPicker multiple={multiple} />
                    </ProviderRS2>
                </Dialog>
            )}
        </DialogContainer>
    );
};

CategoryPickerModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onConfirm: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    multiple: PropTypes.bool.isRequired,
    selectionId: PropTypes.oneOf(['id', 'slug', 'uid', 'urlPath', 'idAndUrlPath', 'uidAndUrlPath']).isRequired
};

const ProductPickerModal = props => {
    const { formatMessage } = useIntl();
    const { show, onConfirm, onClose, multiple, selectionId, enablePreviewDateFilter } = props;
    const [{ pickerSelection }] = useSelectionContext();
    const [, { getSelectionId, getProducts }] = useProductPickerContext();

    const confirmSelection = async () => {
        const products = await getProducts(Array.from(pickerSelection), selectionId);

        const selections = products.map(item => {
            const [value, type] = getSelectionId(item, selectionId);
            return { value, type, item: { ...item, url_key: item.slug } };
        });

        onConfirm(selections);
        onClose();
    };
    return (
        <DialogContainer type="fullscreen">
            {show && (
                <Dialog title={formatMessage(titles.pickProduct)} onClose={onClose} onConfirm={confirmSelection}>
                    <ProviderRS2 theme="light">
                        <ProductPicker
                            multiple={multiple}
                            selectionId={selectionId}
                            enablePreviewDateFilter={enablePreviewDateFilter}
                        />
                    </ProviderRS2>
                </Dialog>
            )}
        </DialogContainer>
    );
};

ProductPickerModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onConfirm: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    multiple: PropTypes.bool.isRequired,
    selectionId: PropTypes.oneOf(['id', 'sku', 'slug', 'combinedSku', 'combinedSlug']).isRequired,
    enablePreviewDateFilter: PropTypes.bool
};

const PickerModal = props => {
    const { multiple, selectionId, targetType, domRoot, enablePreviewDateFilter } = props;
    const { formatMessage } = useIntl();
    const [schemaStatus, schemaError, [config]] = useGraphqlSchemaContext();
    const [show, setShow] = useState(false);

    const onConfirm = selections => {
        const target = document.querySelector(props.target);
        if (!target) {
            return;
        }

        if (selections.length > 0 && selections[0].value) {
            let selectionEvent = new CustomEvent(pickerEvents.selectionEvent, {
                detail: { pickerType: props.type, selection: selections },
                bubbles: true //need to enable event bubbling
                //otherwise $(document).on(event, selector, func) won't catch it
            });

            target.dispatchEvent(selectionEvent);
        }
    };

    const onClose = () => {
        const target = document.querySelector(props.target);
        if (!target) {
            return;
        }

        let closedEvent = new CustomEvent(pickerEvents.closeEvent, {
            detail: { pickerType: props.type },
            bubbles: true //need to enable event bubbling
            //otherwise $(document).on(event, selector, func) won't catch it
        });
        target.dispatchEvent(closedEvent);
    };

    const onError = e => {
        const target = document.querySelector(props.target);
        if (!target) {
            return;
        }

        let errorEvent = new CustomEvent(pickerEvents.errorEvent, {
            detail: { pickerType: props.type, error: e },
            bubbles: true //need to enable event bubbling
            //otherwise $(document).on(event, selector, func) won't catch it
        });
        target.dispatchEvent(errorEvent);
    };

    useEffect(() => {
        if (schemaStatus === 'error') {
            onError({ title: schemaError.title, description: schemaError.description });
        } else if (schemaStatus === 'done') {
            if (!config) {
                onError({
                    title: formatMessage(titles.missingConfigTitle),
                    description: formatMessage(titles.missingConfigDesc)
                });
            } else {
                setShow(props.show);
            }
        }
    }, [schemaStatus, config, props.show]);

    if (!config) {
        // just render nothing, rerender when the config changes
        return <></>;
    }

    return (
        <>
            {props.type === 'category' ? (
                <CategoryPickerContextProvider selectionId={selectionId}>
                    <SelectionContextProvider
                        multiple={multiple}
                        preSelectionKey={selectionId}
                        webComponentTag={'category-field'}
                        target={props.target}
                        targetType={targetType}
                        domRoot={domRoot}
                    >
                        <CategoryPickerModal
                            show={show}
                            multiple={multiple}
                            selectionId={selectionId}
                            onConfirm={onConfirm}
                            onClose={onClose}
                        />
                    </SelectionContextProvider>
                </CategoryPickerContextProvider>
            ) : (
                <ProductPickerContextProvider selectionId={selectionId}>
                    <SelectionContextProvider
                        multiple={multiple}
                        preSelectionKey={selectionId}
                        webComponentTag={'product-field'}
                        target={props.target}
                        targetType={targetType}
                        domRoot={domRoot}
                    >
                        <ProductPickerModal
                            show={show}
                            multiple={multiple}
                            selectionId={selectionId}
                            onConfirm={onConfirm}
                            onClose={onClose}
                            enablePreviewDateFilter={enablePreviewDateFilter}
                        />
                    </SelectionContextProvider>
                </ProductPickerContextProvider>
            )}
        </>
    );
};

PickerModal.propTypes = {
    type: PropTypes.oneOf(['product', 'category']).isRequired,
    show: PropTypes.bool,
    target: PropTypes.string,
    multiple: PropTypes.bool,
    selectionId: PropTypes.oneOf([
        'id',
        'sku',
        'slug',
        'combinedSku',
        'combinedSlug',
        'urlPath',
        'idAndUrlPath',
        'uidAndUrlPath'
    ]),
    targetType: PropTypes.string,
    domRoot: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),
    enablePreviewDateFilter: PropTypes.bool
};

PickerModal.defaultProps = {
    type: 'product',
    show: false,
    multiple: false,
    selectionId: 'slug'
};

export default PickerModal;
